import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
} from '@mui/material';
import {
  Calculate,
  RateReview,
  CheckCircle,
  Publish,
  Edit,
  History,
  ThumbUp,
  ThumbDown,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useSnackbar } from 'notistack';
import { apiGet, apiPost } from '../services/apiClient';
import { LoggerService } from '../services/LoggerService';

interface Fund {
  id: string;
  name: string;
  isin: string;
}

interface ShareClassNAV {
  id: string;
  share_class_name: string;
  currency: string;
  preliminary_nav: number;
  previous_nav: number;
  change_pct: number;
  gross_asset_value: number;
  total_liabilities: number;
  shares_outstanding: number;
  status: string;
  calculated_by: string | null;
  calculated_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  published_by: string | null;
  published_at: string | null;
  share_class_id: string;
}

interface NAVHistoryPoint {
  date: string;
  nav_per_share: number;
}

type NAVStatus = 'preliminary' | 'under_review' | 'one_approval' | 'approved' | 'published' | 'corrected';

const WORKFLOW_STEPS = ['Preliminary', 'Under Review', 'Approved', 'Published'];

const OVERRIDE_FIELDS = [
  { value: 'price', label: 'Price' },
  { value: 'nav_per_share', label: 'NAV Per Share' },
  { value: 'gross_asset_value', label: 'Gross Asset Value' },
  { value: 'total_liabilities', label: 'Total Liabilities' },
  { value: 'accrued_income', label: 'Accrued Income' },
  { value: 'accrued_expenses', label: 'Accrued Expenses' },
];

const REASON_CODES = [
  { value: 'stale_price', label: 'Stale Price' },
  { value: 'corporate_action', label: 'Corporate Action' },
  { value: 'pricing_error', label: 'Pricing Error' },
  { value: 'fair_value_adjustment', label: 'Fair Value Adjustment' },
  { value: 'manual_correction', label: 'Manual Correction' },
];

const DATE_RANGES = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '1y', days: 365 },
  { label: 'All', days: 0 },
];

function getStatusChip(status: NAVStatus): React.ReactElement {
  switch (status) {
    case 'preliminary':
      return <Chip label="Preliminary" color="info" size="small" />;
    case 'under_review':
      return <Chip label="Under Review" color="warning" size="small" />;
    case 'one_approval':
      return <Chip label="One Approval" color="primary" size="small" />;
    case 'approved':
      return <Chip label="Approved" color="success" size="small" />;
    case 'published':
      return <Chip label="Published" color="success" size="small" variant="outlined" icon={<CheckCircle />} />;
    case 'corrected':
      return <Chip label="Corrected" color="error" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
}

function formatCurrency(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

function formatPct(value: number): string {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(4)}%`;
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function NAVWorkflowPage() {
  const { enqueueSnackbar } = useSnackbar();

  // Top controls
  const [funds, setFunds] = useState<Fund[]>([]);
  const [selectedFundId, setSelectedFundId] = useState<string>('');
  const [navDate, setNavDate] = useState<string>(todayISO());
  const [fundsLoading, setFundsLoading] = useState(true);
  const [fundsError, setFundsError] = useState<string | null>(null);

  // Calculation
  const [calculating, setCalculating] = useState(false);

  // Review table
  const [navRows, setNavRows] = useState<ShareClassNAV[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Override dialog
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideRow, setOverrideRow] = useState<ShareClassNAV | null>(null);
  const [overrideField, setOverrideField] = useState('');
  const [overrideOriginalValue, setOverrideOriginalValue] = useState<number>(0);
  const [overrideValue, setOverrideValue] = useState<string>('');
  const [overrideReasonCode, setOverrideReasonCode] = useState('');
  const [overrideReasonDetail, setOverrideReasonDetail] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // Approve dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNavId, setApproveNavId] = useState<string>('');
  const [approveComments, setApproveComments] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);

  // Reject dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNavId, setRejectNavId] = useState<string>('');
  const [rejectComments, setRejectComments] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  // Publish dialog
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishNavId, setPublishNavId] = useState<string>('');
  const [publishSubmitting, setPublishSubmitting] = useState(false);

  // Action loading per row
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  // History
  const [historyShareClassId, setHistoryShareClassId] = useState<string>('');
  const [historyData, setHistoryData] = useState<NAVHistoryPoint[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRange, setHistoryRange] = useState<number>(90);

  // Load funds on mount
  useEffect(() => {
    loadFunds();
  }, []);

  // Load review table when fund or date changes
  useEffect(() => {
    if (selectedFundId && navDate) {
      loadReview();
    }
  }, [selectedFundId, navDate]);

  // Load history when share class or range changes
  useEffect(() => {
    if (historyShareClassId) {
      loadHistory();
    }
  }, [historyShareClassId, historyRange]);

  async function loadFunds() {
    setFundsLoading(true);
    setFundsError(null);
    try {
      const response = await apiGet<{ funds: Fund[] }>('/funds/');
      const list = response.funds || [];
      setFunds(list);
      if (list.length > 0 && !selectedFundId) {
        setSelectedFundId(list[0].id);
      }
      LoggerService.info('[NAVWorkflow] Funds loaded', { count: list.length });
    } catch (err: any) {
      const msg = err?.message || 'Failed to load funds';
      setFundsError(msg);
      LoggerService.error('[NAVWorkflow] Failed to load funds', err);
    } finally {
      setFundsLoading(false);
    }
  }

  async function loadReview() {
    setReviewLoading(true);
    setReviewError(null);
    try {
      const data = await apiGet<ShareClassNAV[]>(`/nav/review/${selectedFundId}`, {
        params: { nav_date: navDate },
      });
      setNavRows(data);
      LoggerService.info('[NAVWorkflow] Review data loaded', { fundId: selectedFundId, rows: data.length });

      // Auto-select first share class for history if none selected
      if (data.length > 0 && !historyShareClassId) {
        setHistoryShareClassId(data[0].share_class_id);
      }
    } catch (err: any) {
      const msg = err?.message || 'Failed to load NAV review data';
      setReviewError(msg);
      LoggerService.error('[NAVWorkflow] Failed to load review', err);
    } finally {
      setReviewLoading(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const params: Record<string, any> = {};
      if (historyRange > 0) {
        const from = new Date();
        from.setDate(from.getDate() - historyRange);
        params.from_date = from.toISOString().slice(0, 10);
      }
      const data = await apiGet<NAVHistoryPoint[]>(`/nav/history/${historyShareClassId}`, { params });
      setHistoryData(data);
      LoggerService.info('[NAVWorkflow] History loaded', { shareClassId: historyShareClassId, points: data.length });
    } catch (err: any) {
      LoggerService.error('[NAVWorkflow] Failed to load history', err);
      enqueueSnackbar('Failed to load NAV history', { variant: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }

  async function handleCalculate() {
    if (!selectedFundId) {
      enqueueSnackbar('Select a fund first', { variant: 'warning' });
      return;
    }
    setCalculating(true);
    try {
      await apiPost(`/nav/calculate/${selectedFundId}`, { nav_date: navDate });
      enqueueSnackbar('NAV calculation started successfully', { variant: 'success' });
      LoggerService.info('[NAVWorkflow] NAV calculation triggered', { fundId: selectedFundId, navDate });
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'NAV calculation failed';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] NAV calculation failed', err);
    } finally {
      setCalculating(false);
    }
  }

  // --- Override ---
  function openOverride(row: ShareClassNAV) {
    setOverrideRow(row);
    setOverrideField('');
    setOverrideOriginalValue(0);
    setOverrideValue('');
    setOverrideReasonCode('');
    setOverrideReasonDetail('');
    setOverrideOpen(true);
  }

  function handleOverrideFieldChange(field: string) {
    setOverrideField(field);
    if (!overrideRow) return;
    const fieldValueMap: Record<string, number> = {
      price: overrideRow.preliminary_nav,
      nav_per_share: overrideRow.preliminary_nav,
      gross_asset_value: overrideRow.gross_asset_value,
      total_liabilities: overrideRow.total_liabilities,
      accrued_income: 0,
      accrued_expenses: 0,
    };
    setOverrideOriginalValue(fieldValueMap[field] ?? 0);
  }

  async function submitOverride() {
    if (!overrideRow || !overrideField || !overrideValue || !overrideReasonCode) {
      enqueueSnackbar('All override fields are required', { variant: 'warning' });
      return;
    }
    setOverrideSubmitting(true);
    try {
      await apiPost('/nav/override', {
        nav_id: overrideRow.id,
        field_overridden: overrideField,
        original_value: overrideOriginalValue,
        override_value: parseFloat(overrideValue),
        reason_code: overrideReasonCode,
        reason_detail: overrideReasonDetail,
      });
      enqueueSnackbar('Override applied successfully', { variant: 'success' });
      LoggerService.info('[NAVWorkflow] Override applied', { navId: overrideRow.id, field: overrideField });
      setOverrideOpen(false);
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'Failed to apply override';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] Override failed', err);
    } finally {
      setOverrideSubmitting(false);
    }
  }

  // --- Submit for Approval ---
  async function handleSubmitForApproval(navId: string) {
    setActionLoadingId(navId);
    try {
      await apiPost(`/nav/submit-for-approval/${navId}`);
      enqueueSnackbar('Submitted for approval', { variant: 'success' });
      LoggerService.info('[NAVWorkflow] Submitted for approval', { navId });
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'Failed to submit for approval';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] Submit for approval failed', err);
    } finally {
      setActionLoadingId(null);
    }
  }

  // --- Approve ---
  function openApprove(navId: string) {
    setApproveNavId(navId);
    setApproveComments('');
    setApproveOpen(true);
  }

  async function submitApprove() {
    setApproveSubmitting(true);
    try {
      await apiPost(`/nav/approve/${approveNavId}`, { comments: approveComments || undefined });
      enqueueSnackbar('NAV approved', { variant: 'success' });
      LoggerService.info('[NAVWorkflow] NAV approved', { navId: approveNavId });
      setApproveOpen(false);
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'Failed to approve NAV';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] Approve failed', err);
    } finally {
      setApproveSubmitting(false);
    }
  }

  // --- Reject ---
  function openReject(navId: string) {
    setRejectNavId(navId);
    setRejectComments('');
    setRejectOpen(true);
  }

  async function submitReject() {
    if (!rejectComments.trim()) {
      enqueueSnackbar('Comments are required when rejecting', { variant: 'warning' });
      return;
    }
    setRejectSubmitting(true);
    try {
      await apiPost(`/nav/reject/${rejectNavId}`, { comments: rejectComments });
      enqueueSnackbar('NAV rejected', { variant: 'warning' });
      LoggerService.info('[NAVWorkflow] NAV rejected', { navId: rejectNavId });
      setRejectOpen(false);
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'Failed to reject NAV';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] Reject failed', err);
    } finally {
      setRejectSubmitting(false);
    }
  }

  // --- Publish ---
  function openPublish(navId: string) {
    setPublishNavId(navId);
    setPublishOpen(true);
  }

  async function submitPublish() {
    setPublishSubmitting(true);
    try {
      await apiPost(`/nav/publish/${publishNavId}`);
      enqueueSnackbar('NAV published successfully', { variant: 'success' });
      LoggerService.info('[NAVWorkflow] NAV published', { navId: publishNavId });
      setPublishOpen(false);
      await loadReview();
    } catch (err: any) {
      const msg = err?.message || 'Failed to publish NAV';
      enqueueSnackbar(msg, { variant: 'error' });
      LoggerService.error('[NAVWorkflow] Publish failed', err);
    } finally {
      setPublishSubmitting(false);
    }
  }

  // Determine the overall pipeline step from the loaded rows
  function getOverallStep(): number {
    if (navRows.length === 0) return 0;
    const statuses = navRows.map((r) => r.status as NAVStatus);
    if (statuses.every((s) => s === 'published')) return 3;
    if (statuses.every((s) => s === 'approved' || s === 'published')) return 2;
    if (statuses.some((s) => s === 'under_review' || s === 'one_approval')) return 1;
    return 0;
  }

  // Loading state for entire page
  if (fundsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (fundsError) {
    return (
      <Box p={3}>
        <Alert severity="error">{fundsError}</Alert>
      </Box>
    );
  }

  if (funds.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No funds available. Create a fund before running NAV calculations.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        NAV Workflow
      </Typography>

      {/* Top Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="fund-select-label">Fund</InputLabel>
              <Select
                labelId="fund-select-label"
                value={selectedFundId}
                label="Fund"
                onChange={(e) => setSelectedFundId(e.target.value)}
              >
                {funds.map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name} ({f.isin})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="NAV Date"
              type="date"
              size="small"
              fullWidth
              value={navDate}
              onChange={(e) => setNavDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              startIcon={calculating ? <CircularProgress size={18} color="inherit" /> : <Calculate />}
              onClick={handleCalculate}
              disabled={calculating || !selectedFundId}
              fullWidth
            >
              {calculating ? 'Calculating...' : 'Calculate NAV'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          NAV Status Pipeline
        </Typography>
        <Stepper activeStep={getOverallStep()} alternativeLabel>
          {WORKFLOW_STEPS.map((label, idx) => (
            <Step key={label} completed={getOverallStep() > idx}>
              <StepLabel
                StepIconProps={{
                  icon:
                    idx === 0 ? <Calculate /> : idx === 1 ? <RateReview /> : idx === 2 ? <CheckCircle /> : <Publish />,
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Review Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            NAV Review — {navDate}
          </Typography>

          {reviewLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {reviewError && <Alert severity="error" sx={{ mb: 2 }}>{reviewError}</Alert>}

          {!reviewLoading && !reviewError && navRows.length === 0 && (
            <Alert severity="info">
              No NAV data for this fund and date. Click &quot;Calculate NAV&quot; to begin.
            </Alert>
          )}

          {!reviewLoading && navRows.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Share Class</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="right">Preliminary NAV</TableCell>
                    <TableCell align="right">Previous NAV</TableCell>
                    <TableCell align="right">Change %</TableCell>
                    <TableCell align="right">GAV</TableCell>
                    <TableCell align="right">Liabilities</TableCell>
                    <TableCell align="right">Shares Out.</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {navRows.map((row) => {
                    const status = row.status as NAVStatus;
                    const isRowLoading = actionLoadingId === row.id;
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.share_class_name}</TableCell>
                        <TableCell>{row.currency}</TableCell>
                        <TableCell align="right">{formatCurrency(row.preliminary_nav, row.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.previous_nav, row.currency)}</TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: row.change_pct >= 0 ? 'success.main' : 'error.main' }}
                        >
                          {formatPct(row.change_pct)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(row.gross_asset_value, row.currency)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.total_liabilities, row.currency)}</TableCell>
                        <TableCell align="right">{row.shares_outstanding.toLocaleString()}</TableCell>
                        <TableCell>{getStatusChip(status)}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} alignItems="center">
                            {isRowLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <>
                                {/* Override — available for preliminary and under_review */}
                                {(status === 'preliminary' || status === 'under_review') && (
                                  <IconButton size="small" title="Override" onClick={() => openOverride(row)}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                )}

                                {/* Submit for Approval — only for preliminary */}
                                {status === 'preliminary' && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleSubmitForApproval(row.id)}
                                  >
                                    Submit
                                  </Button>
                                )}

                                {/* Approve — only for under_review or one_approval */}
                                {(status === 'under_review' || status === 'one_approval') && (
                                  <IconButton
                                    size="small"
                                    color="success"
                                    title="Approve"
                                    onClick={() => openApprove(row.id)}
                                  >
                                    <ThumbUp fontSize="small" />
                                  </IconButton>
                                )}

                                {/* Reject — only for under_review or one_approval */}
                                {(status === 'under_review' || status === 'one_approval') && (
                                  <IconButton
                                    size="small"
                                    color="error"
                                    title="Reject"
                                    onClick={() => openReject(row.id)}
                                  >
                                    <ThumbDown fontSize="small" />
                                  </IconButton>
                                )}

                                {/* Publish — only for approved */}
                                {status === 'approved' && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Publish />}
                                    onClick={() => openPublish(row.id)}
                                  >
                                    Publish
                                  </Button>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Audit trail below table */}
          {navRows.length > 0 && (
            <Box mt={2}>
              <Divider sx={{ mb: 1 }} />
              <Typography variant="caption" color="text.secondary" component="div">
                {navRows
                  .filter((r) => r.calculated_by)
                  .slice(0, 1)
                  .map((r) => (
                    <span key={`calc-${r.id}`}>
                      Calculated by {r.calculated_by} at {formatTimestamp(r.calculated_at)}
                    </span>
                  ))}
                {navRows
                  .filter((r) => r.approved_by)
                  .slice(0, 1)
                  .map((r) => (
                    <span key={`appr-${r.id}`}>
                      {' | '}Approved by {r.approved_by} at {formatTimestamp(r.approved_at)}
                    </span>
                  ))}
                {navRows
                  .filter((r) => r.published_by)
                  .slice(0, 1)
                  .map((r) => (
                    <span key={`pub-${r.id}`}>
                      {' | '}Published by {r.published_by} at {formatTimestamp(r.published_at)}
                    </span>
                  ))}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* NAV History */}
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <History />
            <Typography variant="h6">NAV History</Typography>
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="history-sc-label">Share Class</InputLabel>
                <Select
                  labelId="history-sc-label"
                  value={historyShareClassId}
                  label="Share Class"
                  onChange={(e) => setHistoryShareClassId(e.target.value)}
                >
                  {navRows.map((r) => (
                    <MenuItem key={r.share_class_id} value={r.share_class_id}>
                      {r.share_class_name} ({r.currency})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <Box display="flex" gap={1}>
                {DATE_RANGES.map((dr) => (
                  <Button
                    key={dr.label}
                    size="small"
                    variant={historyRange === dr.days ? 'contained' : 'outlined'}
                    onClick={() => setHistoryRange(dr.days)}
                  >
                    {dr.label}
                  </Button>
                ))}
              </Box>
            </Grid>
          </Grid>

          {historyLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}

          {!historyLoading && historyData.length === 0 && (
            <Alert severity="info">No history data available for this share class.</Alert>
          )}

          {!historyLoading && historyData.length > 0 && (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={historyData} margin={{ top: 5, right: 20, bottom: 5, left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d: string) =>
                    new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                  }
                />
                <YAxis domain={['auto', 'auto']} />
                <Tooltip
                  formatter={(value: number) => [value.toFixed(4), 'NAV/Share']}
                  labelFormatter={(label: string) =>
                    new Date(label).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                  }
                />
                <Line type="monotone" dataKey="nav_per_share" stroke="#1976d2" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Override Dialog */}
      <Dialog open={overrideOpen} onClose={() => setOverrideOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override NAV Value</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <FormControl fullWidth size="small">
              <InputLabel id="override-field-label">Field to Override</InputLabel>
              <Select
                labelId="override-field-label"
                value={overrideField}
                label="Field to Override"
                onChange={(e) => handleOverrideFieldChange(e.target.value)}
              >
                {OVERRIDE_FIELDS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>
                    {f.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Original Value"
              value={overrideOriginalValue}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
            />

            <TextField
              label="Override Value"
              type="number"
              value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ step: '0.0001' }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="reason-code-label">Reason Code</InputLabel>
              <Select
                labelId="reason-code-label"
                value={overrideReasonCode}
                label="Reason Code"
                onChange={(e) => setOverrideReasonCode(e.target.value)}
              >
                {REASON_CODES.map((rc) => (
                  <MenuItem key={rc.value} value={rc.value}>
                    {rc.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Reason Detail"
              value={overrideReasonDetail}
              onChange={(e) => setOverrideReasonDetail(e.target.value)}
              size="small"
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideOpen(false)} disabled={overrideSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitOverride}
            disabled={overrideSubmitting || !overrideField || !overrideValue || !overrideReasonCode}
            startIcon={overrideSubmitting ? <CircularProgress size={18} /> : undefined}
          >
            Apply Override
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve NAV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Confirm approval of this NAV calculation. You may add optional comments.
          </Typography>
          <TextField
            label="Comments (optional)"
            value={approveComments}
            onChange={(e) => setApproveComments(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={approveSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={submitApprove}
            disabled={approveSubmitting}
            startIcon={approveSubmitting ? <CircularProgress size={18} /> : <ThumbUp />}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject NAV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this NAV calculation.
          </Typography>
          <TextField
            label="Comments (required)"
            value={rejectComments}
            onChange={(e) => setRejectComments(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
            required
            error={rejectOpen && rejectComments.trim().length === 0}
            helperText={rejectOpen && rejectComments.trim().length === 0 ? 'Comments are required when rejecting' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={rejectSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={submitReject}
            disabled={rejectSubmitting || !rejectComments.trim()}
            startIcon={rejectSubmitting ? <CircularProgress size={18} /> : <ThumbDown />}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Publish NAV</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            Publishing NAV is irreversible for this date. Continue?
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)} disabled={publishSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitPublish}
            disabled={publishSubmitting}
            startIcon={publishSubmitting ? <CircularProgress size={18} /> : <Publish />}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
